/**
 * Admin Portal → Partners → a tag → Website code (codes platform item C7, owner ruling 20).
 *
 * Builds the copy-paste HTML a partner's developer drops into their site, and the QR code
 * for the same link. Pure (no DOM, no React) so jest can pin every byte that leaves the
 * portal.
 *
 * Design: docs/plans/PARTNER_CODES_PLATFORM_DESIGN.md §7 —
 *   - a self-contained HTML block: no script, no stylesheet, no image and no font loaded
 *     from anywhere at runtime (the design allowed a badge image; nothing needs one, so
 *     the partner's page makes no request to us until a visitor actually taps);
 *   - every link points at the tag's `https://coachcedar.com/go/<tag>` URL, so the click is
 *     recorded on coachcedar.com exactly as today (09-17 P6: every partner link resolves on
 *     coachcedar.com) — there is no second tracking path;
 *   - variants: a text link, a button, a button + an inline SVG QR code.
 *
 * Safety: the ONLY inputs are the link URL (server-built, and re-checked here against the
 * exact shapes `services.partner_admin.build_link_url` emits), the tag (TAG_RE) and a
 * button label an admin may type. Every value is HTML-escaped where it lands; the URL is
 * refused unless it is one of the /go shapes; the label loses control characters and is
 * capped. The QR markup contains only numbers.
 *
 * The QR encoder is qrcode-generator 2.0.4 (MIT), vendored under ./vendor (see its header
 * for why it is not an npm dependency).
 */

import { LINK_DESTINATIONS, PARTNER_CODE_RE, SITE_BASE, TAG_RE } from './partnersLogic';
import qrcodeGenerator from './vendor/qrcodeGenerator';

/** The part of qrcode-generator's API this module uses (the vendored file is untyped). */
interface QrCodeLike {
  addData(data: string, mode?: 'Byte'): void;
  make(): void;
  getModuleCount(): number;
  isDark(row: number, col: number): boolean;
}
const qrcode = qrcodeGenerator as unknown as (typeNumber: 0, errorCorrectionLevel: 'L' | 'M' | 'Q' | 'H') => QrCodeLike;

// ── escaping ─────────────────────────────────────────────────────────────────

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '`': '&#96;',
};

/** Escape text for an HTML text node OR a double-quoted attribute value. */
export function escapeHtml(value: string): string {
  return String(value).replace(/[&<>"'`]/g, (c) => HTML_ESCAPES[c]);
}

// ── the link ─────────────────────────────────────────────────────────────────

/** A regex's source without its ^ … $ anchors. */
function unanchored(re: RegExp): string {
  return re.source.replace(/^\^/, '').replace(/\$$/, '');
}

const TAG_BODY = unanchored(TAG_RE);
/**
 * The `p=` value is the partner code lower-cased (build_link_url: `validate_partner_code(...).lower()`),
 * so its rule is PARTNER_CODE_RE lower-cased. Lower-casing a regex source is only safe while it has
 * no escapes (`\D` would become `\d`), and the source alone carries the rule only while the regex
 * has no flags (an `i` would be lost here and change what it accepts): refuse to load rather than
 * build a wrong rule.
 */
if (/\\/.test(PARTNER_CODE_RE.source) || PARTNER_CODE_RE.flags !== '') {
  throw new Error('PARTNER_CODE_RE has an escape or a flag; derive the p= rule by hand');
}
const PARTNER_PARAM_BODY = unanchored(PARTNER_CODE_RE).toLowerCase();
const SIMPLE_DESTINATIONS = LINK_DESTINATIONS.filter((d) => d !== 'home' && d !== 'with');
const SITE_RE_BASE = SITE_BASE.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

/**
 * The exact URL shapes `services.partner_admin.build_link_url` emits:
 *   https://coachcedar.com/go/<tag>
 *   https://coachcedar.com/go/<tag>?d=<about|blog|support>
 *   https://coachcedar.com/go/<tag>?d=with&p=<partner code, lowercase>
 * Nothing else is accepted: no other host, no userinfo, no port, no fragment, no extra
 * parameters (a partner link never carries anything the /go page does not read).
 */
export const GO_URL_RE = new RegExp(
  `^${SITE_RE_BASE}/go/(${TAG_BODY})` +
    `(?:\\?d=(?:${SIMPLE_DESTINATIONS.join('|')})|\\?d=with&p=(?:${PARTNER_PARAM_BODY}))?$`,
);

export type GoUrlCheck = { ok: true; url: string; tag: string } | { ok: false; reason: string };

export function checkGoUrl(url: string): GoUrlCheck {
  if (typeof url !== 'string' || !url) return { ok: false, reason: 'No link.' };
  const m = GO_URL_RE.exec(url);
  if (!m) return { ok: false, reason: `Not a partner link: website code only points at ${SITE_BASE}/go/<tag>.` };
  return { ok: true, url, tag: m[1] };
}

// ── the button label ─────────────────────────────────────────────────────────

export const DEFAULT_BUTTON_LABEL = 'Get Coach Cedar';
/** In user-perceived characters (grapheme clusters). */
export const BUTTON_LABEL_MAX = 60;

/**
 * Invisible formatting characters, removed outright (not turned into spaces): the bidi marks,
 * embeddings, overrides and isolates (LRM/RLM/ALM, U+202A–202E, U+2066–2069 — the
 * "Trojan Source" set), the zero-width space, the word joiner and invisible operators
 * (U+2060–2064), the byte-order mark, and the language tag and unassigned tag characters
 * (U+E0000–E001F). Written as escapes: this file must never contain one of them raw (gate-pinned).
 *
 * KEPT (review of #4080, round 2, LOW-1): the zero-width non-joiner and joiner (U+200C/200D) —
 * emoji sequences (a runner, a rainbow flag) are joined with ZWJ, and Persian and several Indic
 * scripts spell words with ZWNJ — and the tag characters of a flag (keepFlagTagSequences below).
 */
const INVISIBLE_FORMAT_RE = /[\u061c\u200b\u200e\u200f\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff\u{e0000}-\u{e001f}]/gu;

/**
 * Tag characters (U+E0020–E007F) survive only as an emoji tag sequence on the black flag
 * U+1F3F4: a CLDR subdivision id spelled in tag characters, then CANCEL TAG U+E007F (UTS #51
 * ED-14a — the England, Scotland and Wales flags). Any other tag character is removed: a run of
 * them spells invisible ASCII ("tag smuggling"), and a malformed flag renders as the plain black
 * flag anyway.
 *
 * The id's grammar is CLDR's own (UTS #35 `unicode_subdivision_id`), strict and lowercase as UTS
 * #51 spells it (review of #4201, NIT-C7-b — it was any 1–7 tag characters): a region of
 * FLAG_REGION_ALPHA_LEN letters or FLAG_REGION_DIGIT_LEN digits, then a suffix of FLAG_SUFFIX_MIN
 * to FLAG_SUFFIX_MAX letters or digits. FLAG_TAG_SPEC_RE is built from these constants, and
 * FLAG_TAG_SPEC_MAX (the longest id) from them too.
 */
export const FLAG_REGION_ALPHA_LEN = 2;
export const FLAG_REGION_DIGIT_LEN = 3;
export const FLAG_SUFFIX_MIN = 1;
export const FLAG_SUFFIX_MAX = 4;
export const FLAG_TAG_SPEC_MAX = Math.max(FLAG_REGION_ALPHA_LEN, FLAG_REGION_DIGIT_LEN) + FLAG_SUFFIX_MAX;
/** Tag LATIN SMALL LETTER A–Z and tag DIGIT ZERO–NINE, as regex class ranges (escapes only). */
const TAG_LOWER = '\\u{e0061}-\\u{e007a}';
const TAG_DIGIT = '\\u{e0030}-\\u{e0039}';
const TAG_RUN_RE = /(\u{1f3f4})?([\u{e0020}-\u{e007f}]+)/gu;
const FLAG_TAG_SPEC_RE = new RegExp(
  `^(?:[${TAG_LOWER}]{${FLAG_REGION_ALPHA_LEN}}|[${TAG_DIGIT}]{${FLAG_REGION_DIGIT_LEN}})` +
    `[${TAG_LOWER}${TAG_DIGIT}]{${FLAG_SUFFIX_MIN},${FLAG_SUFFIX_MAX}}\\u{e007f}$`,
  'u',
);

/** Keep a flag's tag sequence; drop every other tag character (the flag glyph itself stays). */
function keepFlagTagSequences(text: string): string {
  return text.replace(TAG_RUN_RE, (_m, flag: string | undefined, run: string) =>
    flag && FLAG_TAG_SPEC_RE.test(run) ? flag + run : flag ?? '');
}

/**
 * Lone surrogates become U+FFFD (`String.prototype.toWellFormed`, or the same by hand where the
 * engine lacks it). Done FIRST (review of #4201, LOW-C7-a): a lone high and a lone low surrogate
 * with a removed character between them (a `{`, an invisible mark) would otherwise be joined
 * into one code point — a tag character — AFTER the tag rule ran.
 */
const SURROGATES_RE = /[\ud800-\udbff][\udc00-\udfff]|[\ud800-\udfff]/g;
export function toWellFormed(text: string): string {
  const native = (String.prototype as unknown as { toWellFormed?: (this: string) => string }).toWellFormed;
  if (typeof native === 'function') return native.call(text);
  return text.replace(SURROGATES_RE, (m) => (m.length === 2 ? m : '\ufffd'));
}

/** C0/C1 controls (newlines and tabs included): treated as a word break. */
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f]/g;
/** Template delimiters: `{{ }}` / `${ }` must not reach a partner's page, where a site builder may expand them. */
const TEMPLATE_BRACES_RE = /[{}]/g;

/** Split into user-perceived characters (Intl.Segmenter where available, else code points). */
export function graphemes(text: string): string[] {
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: 'grapheme' }) => { segment(t: string): Iterable<{ segment: string }> } }).Segmenter;
  if (typeof Seg === 'function') {
    return Array.from(new Seg(undefined, { granularity: 'grapheme' }).segment(text), (x) => x.segment);
  }
  return Array.from(text);
}

/** At most `max` user-perceived characters, never splitting one. */
export function capGraphemes(text: string, max: number): string {
  const g = graphemes(text);
  return g.length <= max ? text : g.slice(0, max).join('');
}

/**
 * An admin-typed label, made safe to render as text: invisible formatting characters, stray
 * tag characters and `{`/`}` removed, controls turned into spaces, whitespace collapsed, trimmed, capped at
 * BUTTON_LABEL_MAX user-perceived characters. Blank → the default. (It is escaped again
 * where it lands; this only keeps it readable and inert.)
 */
export function cleanButtonLabel(raw: string | null | undefined): string {
  // Well-formed and brace-free BEFORE the invisible and tag rules (review of #4201, LOW-C7-a):
  // no later removal can join two halves into a character those rules never saw.
  const text = keepFlagTagSequences(
    toWellFormed(String(raw ?? ''))
      .replace(TEMPLATE_BRACES_RE, '')
      .replace(INVISIBLE_FORMAT_RE, ''),
  )
    .replace(CONTROL_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return DEFAULT_BUTTON_LABEL;
  // Where Intl.Segmenter is missing the cap counts code points and can cut a flag's tag
  // sequence in two: the flag rule runs again on what is left (review of #4201, LOW-C7-a).
  return keepFlagTagSequences(capGraphemes(text, BUTTON_LABEL_MAX)).trim();
}

// ── the QR code ──────────────────────────────────────────────────────────────

/** The marketing site's QR (PhoneHandoff.tsx) uses level M and Cedar green on white. */
export const QR_ERROR_CORRECTION = 'M' as const;
export const QR_QUIET_ZONE = 4; // modules — the QR spec's minimum
export const QR_DARK = '#1A3A1F';
export const QR_LIGHT = '#ffffff';

/**
 * The module matrix (true = dark), without the quiet zone.
 *
 * ASCII only: the vendored encoder's byte mode keeps the LOW 8 BITS of each UTF-16 unit
 * (`charCodeAt(i) & 0xff`, i.e. Latin-1), so any other character would be encoded as a
 * different one — a QR that scans to a different URL. Every caller passes a checked /go
 * link (GO_URL_RE admits only ASCII); anything else is refused here too.
 */
export function qrMatrix(text: string): boolean[][] {
  if (!/^[\x20-\x7e]+$/.test(text)) throw new Error('A QR code can only be made for a printable-ASCII link.');
  const qr = qrcode(0, QR_ERROR_CORRECTION);
  qr.addData(text, 'Byte');
  qr.make();
  const n = qr.getModuleCount();
  const rows: boolean[][] = [];
  for (let r = 0; r < n; r++) {
    const row: boolean[] = [];
    for (let c = 0; c < n; c++) row.push(qr.isDark(r, c));
    rows.push(row);
  }
  return rows;
}

/** One SVG path, one unit square per dark module, offset by the quiet zone. */
export function qrPath(matrix: boolean[][]): string {
  const parts: string[] = [];
  for (let r = 0; r < matrix.length; r++) {
    for (let c = 0; c < matrix[r].length; c++) {
      if (matrix[r][c]) parts.push(`M${c + QR_QUIET_ZONE} ${r + QR_QUIET_ZONE}h1v1h-1z`);
    }
  }
  return parts.join('');
}

export interface QrSvgOptions {
  /** Rendered width and height in CSS px (the viewBox scales). */
  size?: number;
  /** A standalone .svg file (xmlns + XML declaration) rather than inline markup. */
  standalone?: boolean;
}

/**
 * The QR for a /go link as SVG markup. Refuses anything that is not a /go link, so a QR
 * can never be made for a different site from this screen.
 */
export function qrSvg(url: string, opts: QrSvgOptions = {}): string {
  const checked = checkGoUrl(url);
  if (!checked.ok) throw new Error(checked.reason);
  const matrix = qrMatrix(checked.url);
  const dim = matrix.length + QR_QUIET_ZONE * 2;
  const size = Math.max(64, Math.min(2048, Math.round(opts.size ?? 132)));
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" width="${size}" height="${size}"` +
    ` role="img" aria-label="${escapeHtml(`QR code: ${displayUrl(checked.url)}`)}" shape-rendering="crispEdges">` +
    `<rect width="${dim}" height="${dim}" fill="${QR_LIGHT}"/>` +
    `<path fill="${QR_DARK}" d="${qrPath(matrix)}"/>` +
    `</svg>`;
  return opts.standalone ? `<?xml version="1.0" encoding="UTF-8"?>\n${svg}\n` : svg;
}

/** Pixel plan for a PNG download: module scale so the image is about `target` px square. */
export function qrPngPlan(url: string, target = 1024): { matrix: boolean[][]; scale: number; px: number; quiet: number } {
  const checked = checkGoUrl(url);
  if (!checked.ok) throw new Error(checked.reason);
  const matrix = qrMatrix(checked.url);
  const dim = matrix.length + QR_QUIET_ZONE * 2;
  const scale = Math.max(1, Math.floor(target / dim));
  return { matrix, scale, px: dim * scale, quiet: QR_QUIET_ZONE };
}

/** `https://coachcedar.com/go/x` → `coachcedar.com/go/x` (for captions and labels). */
export function displayUrl(url: string): string {
  return url.replace(/^https:\/\//, '');
}

// ── the website code ─────────────────────────────────────────────────────────

export const WEBSITE_CODE_VARIANTS = ['button_qr', 'button', 'text_link'] as const;
export type WebsiteCodeVariant = (typeof WEBSITE_CODE_VARIANTS)[number];

export const WEBSITE_CODE_VARIANT_LABELS: Record<WebsiteCodeVariant, string> = {
  button_qr: 'Button + QR code',
  button: 'Button only',
  text_link: 'Text link',
};

const FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
const BUTTON_STYLE =
  `display:inline-block;padding:12px 22px;border-radius:999px;background:${QR_DARK};color:#ffffff;` +
  `font:600 16px/1.2 ${FONT};text-decoration:none;`;
const LINK_STYLE = `color:${QR_DARK};font-weight:600;`;
const WRAP_STYLE = 'display:inline-flex;flex-wrap:wrap;align-items:center;gap:16px;';
const QR_BOX_STYLE = `display:inline-block;text-align:center;font:400 12px/1.3 ${FONT};color:#4b5563;`;

export const QR_CAPTION = 'Scan with your phone';

export interface WebsiteCodeInput {
  url: string;
  variant: WebsiteCodeVariant;
  label?: string | null;
}

export type WebsiteCodeResult = { ok: true; html: string; tag: string } | { ok: false; reason: string };

/**
 * The HTML a partner pastes. Opens in a new tab, sends no referrer (the /go page needs
 * none) and no opener. `data-cedar-tag` names the tag for the partner's developer; it is
 * never read by us.
 */
export function buildWebsiteCode(input: WebsiteCodeInput): WebsiteCodeResult {
  const checked = checkGoUrl(input.url);
  if (!checked.ok) return checked;
  if (!(WEBSITE_CODE_VARIANTS as readonly string[]).includes(input.variant)) {
    return { ok: false, reason: 'Unknown website code variant.' };
  }
  const href = escapeHtml(checked.url);
  const tag = escapeHtml(checked.tag);
  const label = escapeHtml(cleanButtonLabel(input.label));
  const anchor = (style: string) =>
    `<a href="${href}" target="_blank" rel="noopener noreferrer" data-cedar-tag="${tag}" style="${style}">${label}</a>`;

  const header = '<!-- Coach Cedar partner link. Paste as-is: the visit is counted on coachcedar.com. -->';
  let body: string;
  if (input.variant === 'text_link') {
    body = anchor(LINK_STYLE);
  } else if (input.variant === 'button') {
    body = anchor(BUTTON_STYLE);
  } else {
    body =
      `<div style="${WRAP_STYLE}">\n` +
      `  ${anchor(BUTTON_STYLE)}\n` +
      `  <div style="${QR_BOX_STYLE}">\n` +
      `    ${qrSvg(checked.url, { size: 132 })}\n` +
      `    <div>${escapeHtml(QR_CAPTION)}</div>\n` +
      `  </div>\n` +
      `</div>`;
  }
  return { ok: true, html: `${header}\n${body}\n`, tag: checked.tag };
}

/**
 * The admin preview's document: the snippet exactly as copied, in a body with pointer events
 * off (an ordinary click, a hover or a drag does nothing). The iframe adds an empty sandbox.
 * This does not stop a phone scanning the QR — that is a real visit.
 */
export function previewDocument(html: string): string {
  return `<!doctype html><html><body style="margin:16px;background:#ffffff;pointer-events:none;user-select:none">${html}</body></html>`;
}

/** A safe file name for a QR download (the tag is TAG_RE-checked). */
export function qrFileName(url: string, ext: 'svg' | 'png'): string {
  const checked = checkGoUrl(url);
  if (!checked.ok) return `coach-cedar-link-qr.${ext}`;
  const tag = checked.tag;
  const dest = /\?d=([a-z]+)/.exec(checked.url)?.[1];
  return `coach-cedar-${tag}${dest ? `-${dest}` : ''}-qr.${ext}`;
}
